import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useShows, useCurrentUser } from '@/api/hooks'
import { useCurrentShow } from '@/context/current-show'
import { parseApiErrorStatus } from '@/lib/api-errors'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { STATUS_LABELS } from '@/lib/show-status'
import { formatDateRangeDateOnly } from '@/lib/date'

const tabFilters = [
  { label: 'All', value: 'all' },
  { label: 'Accepting entries', value: 'accepting_entries' },
  { label: 'Judging', value: 'judging' },
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'Finalized', value: 'finalized' },
]

export default function ShowsPage() {
  const user = useCurrentUser()
  const isAdmin = !user.isLoading && user.data?.isAdmin === true
  const [tab, setTab] = useState<
    'all' | 'accepting_entries' | 'judging' | 'upcoming' | 'finalized'
  >('all')
  const { currentShow, setCurrentShow } = useCurrentShow()
  const navigate = useNavigate()

  const showsQuery = useShows(
    tab === 'all'
      ? { orderBy: 'startDate:asc' }
      : tab === 'accepting_entries'
        ? { status: 'accepting_entries', orderBy: 'startDate:asc' }
        : tab === 'judging'
          ? { status: 'judging', orderBy: 'startDate:asc' }
          : tab === 'upcoming'
            ? { when: 'upcoming', orderBy: 'startDate:asc' }
            : { status: 'finalized', orderBy: 'startDate:desc' }
  )

  // The server refuses the list (403) for an account with no show role; any other failure says
  // nothing about assignment.
  const noAssignedShows =
    !isAdmin &&
    tab === 'all' &&
    (!showsQuery.isError || parseApiErrorStatus(showsQuery.error) === 403)

  const [searchTerm, setSearchTerm] = useState('')

  const shows = useMemo(() => showsQuery.data ?? [], [showsQuery.data])
  const filteredShows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return shows
    return shows.filter(show => show.name.toLowerCase().includes(term))
  }, [shows, searchTerm])

  return (
    <div className="space-y-8">
      <section className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Shows</h1>
          <p className="text-muted-foreground">
            Browse, set up, and run shows. Use the tabs to focus on accepting entries, judging,
            upcoming, or finalized events.
          </p>
        </div>
        {isAdmin && (
          <Button asChild variant="outline">
            <Link to="/admin/show-import">Import show</Link>
          </Button>
        )}
        {isAdmin && (
          <Button asChild size="lg" className="self-start md:self-auto">
            <Link to="/shows/new">Create New Show</Link>
          </Button>
        )}
      </section>

      <Tabs value={tab} onValueChange={value => setTab(value as typeof tab)}>
        <TabsList>
          {tabFilters.map(filter => (
            <TabsTrigger key={filter.value} value={filter.value}>
              {filter.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={tab} className="mt-4 space-y-3">
          {showsQuery.isLoading ? (
            <ShowsSkeleton />
          ) : (
            <>
              {shows.length > 0 && (
                <div className="flex justify-end">
                  <Input
                    value={searchTerm}
                    onChange={event => setSearchTerm(event.target.value)}
                    placeholder="Search shows"
                    aria-label="Search shows"
                    className="w-full sm:max-w-xs"
                  />
                </div>
              )}
              {shows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {noAssignedShows
                    ? 'No shows assigned to you. Ask an administrator or your show organizer to assign you to a show.'
                    : 'No shows yet for this filter.'}
                </p>
              ) : filteredShows.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No shows match “{searchTerm.trim()}”.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border bg-background">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredShows.map(show => (
                        <TableRow
                          key={show.id ?? show.name}
                          data-testid={`show-row-${show.id ?? show.name}`}
                          className={cn(
                            'transition-colors hover:bg-muted/50',
                            currentShow?.id === show.id ? 'bg-primary/5' : undefined
                          )}
                        >
                          <TableCell className="w-full align-top">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2 font-medium">
                                <span>{show.name}</span>
                                {currentShow?.id === show.id ? <Badge>Current</Badge> : null}
                              </div>
                              <span className="text-sm text-muted-foreground">
                                {formatDateRangeDateOnly(show.startDate, show.endDate)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                show.status === 'accepting_entries' || show.status === 'judging'
                                  ? 'default'
                                  : show.status === 'finalized'
                                    ? 'secondary'
                                    : 'outline'
                              }
                            >
                              {STATUS_LABELS[show.status] ?? show.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{show.location ?? '—'}</TableCell>
                          <TableCell className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setCurrentShow(show)

                                navigate(`/${show.slug}/setup`)
                              }}
                            >
                              Show Setup
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => {
                                setCurrentShow(show)

                                const path = show.slug ? `/${show.slug}/run` : '/run'
                                navigate(path)
                              }}
                            >
                              Run Show
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

function ShowsSkeleton() {
  return (
    <div className="space-y-2">
      {[1, 2, 3].map(key => (
        <div key={key} className="flex items-center justify-between rounded-md border p-3">
          <div className="space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
          <Skeleton className="h-9 w-32" />
        </div>
      ))}
    </div>
  )
}
